import { type Member, type MemberPasswordResetResult, PASSWORD_RESET_ADMIN_TTL_HOURS } from '@epc/contracts';
import { useMutation } from '@tanstack/react-query';
import { KeyRound, MailCheck } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { ErrorBox } from '../../ui/feedback';

const TTL = PASSWORD_RESET_ADMIN_TTL_HOURS === 24 ? 'сутки' : `${PASSWORD_RESET_ADMIN_TTL_HOURS} ч.`;

/**
 * «Сбросить пароль» в карточке сотрудника (ДС-19): портал присылает человеку письмо со ссылкой для нового пароля.
 * Ссылка уходит только на его почту, администратор её не видит; прежний пароль работает, пока человек не задаст новый.
 */
export function MemberPasswordReset({ member }: { member: Member }) {
  const me = useMe();
  const [asking, setAsking] = useState(false);
  const send = useMutation({
    mutationFn: () =>
      api<MemberPasswordResetResult>(`/admin/members/${member.membershipId}/password-reset`, {
        method: 'POST',
      }),
    onSuccess: () => setAsking(false),
  });
  // В демо-компании письма не отправляются
  if (me.tenant?.isDemo) return null;
  const ownerLocked = member.isOwner && !me.tenant?.isOwner;
  const done = send.data;

  return (
    <section
      className="flex flex-col gap-2 rounded-md border border-line px-4 py-3"
      aria-label="Вход в портал"
    >
      <h3 className="text-[14px] font-medium text-ink">Вход в портал</h3>
      {ownerLocked ? (
        <p className="text-[13px] text-ink-2">
          Пароль владельца компании сбрасывает только он сам: на странице входа — «Забыли пароль?».
        </p>
      ) : member.status !== 'active' ? (
        <p className="text-[13px] text-ink-2">
          Доступ отключён. Чтобы человек снова вошёл, откройте доступ и сохраните, затем отправьте ссылку для
          нового пароля.
        </p>
      ) : done ? (
        <p className="flex gap-2 rounded-md bg-success-soft px-3 py-2.5 text-[14px] text-ink" role="status">
          <MailCheck className="mt-0.5 size-5 shrink-0 text-success" aria-hidden />
          <span>
            Ссылка для нового пароля отправлена на {done.email}. Действует до {dateTime(done.expiresAt)} и
            срабатывает один раз.
          </span>
        </p>
      ) : asking ? (
        <div className="flex flex-col gap-2">
          <p className="text-[14px] text-ink">
            Отправить на {member.email} ссылку для нового пароля? Прежний пароль работает, пока человек не
            задаст новый.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" loading={send.isPending} onClick={() => send.mutate()}>
              Отправить ссылку
            </Button>
            <Button variant="ghost" onClick={() => setAsking(false)}>
              Не отправлять
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-start gap-2">
          <p className="text-[13px] text-ink-2">
            Не может войти или забыл пароль — портал пришлёт ему письмо со ссылкой. Ссылка действует {TTL}, вы
            её не увидите.
          </p>
          <Button icon={<KeyRound className="size-4" />} onClick={() => setAsking(true)}>
            Сбросить пароль
          </Button>
        </div>
      )}
      <ErrorBox error={send.error} />
    </section>
  );
}
